/**
 * CypAsdFunctionalImpairmentPanel — Functional Impairment assessment panel.
 *
 * Assesses the real-world impact of autistic traits across six life domains:
 * Social, Educational, Home, Emotional, Community, and Self-care.
 *
 * Each domain card has:
 *   - Title + evidence dropdown inline in the header row
 *   - Compact indicator checklists (medium-weight text, tight spacing)
 *   - DictationTextArea for free-text "Other" observations
 *
 * Maps to DSM-5 Criterion D — symptoms cause clinically significant
 * impairment in social, occupational, or other important areas.
 */

import React from 'react';
import { ChevronLeftIcon } from '@heroicons/react/solid';
import DictationTextArea from '../../../../components/DictationTextArea';
import {
  EVIDENCE_OPTIONS,
  FUNCTIONAL_IMPAIRMENT_DOMAINS,
  FUNCTIONAL_IMPAIRMENT_LEAD_QUESTION,
} from '../config/cypAsdSSIConfig';

export default function CypAsdFunctionalImpairmentPanel({
  fiData,
  onFieldChange,
  onNavigate,
  criteria,
}) {
  const val = (fieldId) => fiData?.[fieldId] ?? null;

  const lastAvailableCriterion = [...criteria].reverse().find(c => c.available);

  const renderDomainCard = (domain) => {
    const evidenceField = `${domain.id}_evidence`;
    const indicatorsField = `${domain.id}_indicators`;
    const otherField = `${domain.id}_other`;
    const selectedEvidence = val(evidenceField) || '';
    const selectedIndicators = val(indicatorsField) || [];

    const toggleIndicator = (indicator) => {
      const next = selectedIndicators.includes(indicator)
        ? selectedIndicators.filter(i => i !== indicator)
        : [...selectedIndicators, indicator];
      onFieldChange(indicatorsField, next);
    };

    return (
      <div key={domain.id} className="p-5 bg-gray-50 rounded-2xl border border-gray-200 text-left">
        {/* Domain title + evidence dropdown */}
        <div className="flex items-center gap-4 mb-4">
          <h3 className="text-slate-700 text-lg font-semibold">{domain.title}</h3>
          <div className="flex-1" />
          <div className="text-slate-700 text-sm font-medium flex-shrink-0">Impairment:</div>
          <select
            value={selectedEvidence}
            onChange={(e) => onFieldChange(evidenceField, e.target.value || null)}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-400 bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 min-w-[150px]"
            aria-label={`Impairment evidence for ${domain.title}`}
          >
            <option value="">— Select —</option>
            {EVIDENCE_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>

        {/* Indicator checkboxes — compact, medium weight */}
        <div className="flex flex-col mb-3">
          {domain.indicators.map((indicator, i) => {
            const isChecked = selectedIndicators.includes(indicator);
            return (
              <label
                key={i}
                className="flex items-start gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleIndicator(indicator)}
                  className="checkbox mt-1"
                />
                <span className="text-slate-700 text-sm font-medium">
                  {indicator}
                </span>
              </label>
            );
          })}
        </div>

        {/* Free-text notes for this domain */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          <label className="text-slate-700 text-sm font-semibold block mb-1 text-left">
            Clinical Notes
          </label>
          <DictationTextArea
            value={val(otherField) || ''}
            onChange={(v) => onFieldChange(otherField, v)}
            canEdit={true}
            ariaLabel={`${domain.title} — other impairment`}
            minHeight="4rem"
            placeholder="Other observations…"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex-grow p-6 text-left">
      {/* Navigation */}
      <div className="flex justify-between items-center mb-4">
        {lastAvailableCriterion ? (
          <button
            onClick={() => onNavigate(lastAvailableCriterion.id)}
            className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
          >
            <ChevronLeftIcon className="w-5 h-5 mr-2" />
            <span className="font-bold text-sm">Back to {lastAvailableCriterion.id}</span>
          </button>
        ) : <div />}
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-gray-900 text-2xl font-bold mb-2 text-center">
          Functional Impairment
        </h1>
        <div className="p-3 bg-primary/5 rounded-lg border border-primary/15">
          <p className="text-primary text-sm italic text-left">
            &ldquo;{FUNCTIONAL_IMPAIRMENT_LEAD_QUESTION}&rdquo;
          </p>
        </div>
      </div>

      {/* Domain cards — two-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        {FUNCTIONAL_IMPAIRMENT_DOMAINS.map(domain => renderDomainCard(domain))}
      </div>      

      {/* Bottom navigation */}
      <div className="flex justify-start items-center mt-8 pt-6 border-t border-gray-200">
        {lastAvailableCriterion && (
          <button
            onClick={() => onNavigate(lastAvailableCriterion.id)}
            className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
          >
            <ChevronLeftIcon className="w-5 h-5 mr-2" />
            <span className="font-bold text-sm">{lastAvailableCriterion.fullTitle}</span>
          </button>
        )}
      </div>
    </div>
  );
}
